import {
  Drawer,
  Group,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconAlertTriangle } from "@tabler/icons-react"
import { SortingState } from "@tanstack/react-table"
import { Button, EqualWidthGrid } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { FindProdSiteFieldDefsByOrgIdResponse } from "@tfg/types/schemas/modules/traceability/dtos/find-prod-site-field-defs-by-org-id.dto"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { ProductionSiteFieldDefinitionUpdateForm } from "~/pages/Admin/MasterLists/ProductionSiteFieldDefinitions/ProductionSiteFieldDefinitionUpdateForm"
import { useAddProductionSiteFieldDefinitionToProductsMutation } from "~/pages/Admin/MasterLists/ProductionSiteFieldDefinitions/hooks/useAddProductionSiteFieldDefinitionToProductsMutation"
import { useDeleteProductionSiteFieldDefinitionMutation } from "~/pages/Admin/MasterLists/ProductionSiteFieldDefinitions/hooks/useDeleteProductionSiteFieldDefinitionMutation"

import { useColumns } from "./columns"

type Props = {
  prodSiteFieldDefs: FindProdSiteFieldDefsByOrgIdResponse
  orgId: string
  activityNames: string[]
}

export function ProductionSiteFieldDefititionsTable({
  prodSiteFieldDefs,
  orgId,
  activityNames: activities,
}: Props) {
  const { t } = useTranslation(["admin", "common"])

  const { mutate: deleteProductionSiteField } =
    useDeleteProductionSiteFieldDefinitionMutation({
      onSuccess: () => {
        close()
      },
    })

  const { mutate: addToProducts } =
    useAddProductionSiteFieldDefinitionToProductsMutation()

  const [opened, { open, close }] = useDisclosure()
  const [
    openedUpdateDrawer,
    { open: openUpdateDrawer, close: closeUpdateDrawer },
  ] = useDisclosure()

  const [
    selectedProductionSiteFieldDefinitionId,
    setSelectedProductionSiteFieldDefinitionId,
  ] = useState<string>("")

  const [
    selectedProductionSiteFieldDefinitionForUpdate,
    setSelectedProductionSiteFieldDefinitionForUpdate,
  ] = useState<FindProdSiteFieldDefsByOrgIdResponse[number]>()

  const handleDelete = (id: string) => {
    setSelectedProductionSiteFieldDefinitionId(id)
    open()
  }

  const handleUpdate = (data: FindProdSiteFieldDefsByOrgIdResponse[number]) => {
    setSelectedProductionSiteFieldDefinitionForUpdate(data)
    openUpdateDrawer()
  }

  const handleAddToProducts = (id: string) => {
    addToProducts({ prodSiteFieldId: id, orgId })
  }

  const columns = useColumns({
    handleDelete,
    handleUpdate,
    handleAddToProducts,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ])

  const table = useCustomReactTable({
    data: prodSiteFieldDefs,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  })

  const { colors } = useMantineTheme()

  return (
    <>
      <CustomTable table={table} pagination={false} />

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group justify="center">
            <IconAlertTriangle color={colors.yellow[5]} />
            <Text fz="lg">Warning</Text>
          </Group>
        }
      >
        <Stack style={{ textAlign: "center" }}>
          <Text fz="lg">
            This productuion site field <strong>might have some</strong>{" "}
            products of this organization associated with it. Are you sure you
            want to delete this custom field?
          </Text>
          <Group justify="center">
            <EqualWidthGrid>
              <Button
                onClick={close}
                variant="secondary-critical"
                fullWidth={true}
              >
                Cancel
              </Button>
              <Button
                variant="primary-critical"
                fullWidth={true}
                onClick={() =>
                  deleteProductionSiteField({
                    id: selectedProductionSiteFieldDefinitionId,
                    organizationId: orgId,
                  })
                }
              >
                Delete
              </Button>
            </EqualWidthGrid>
          </Group>
        </Stack>
      </Modal>
      <Drawer
        position="right"
        opened={openedUpdateDrawer}
        onClose={closeUpdateDrawer}
        size="lg"
        title={<Text fz="lg">{t("prodSiteFieldDefs.create")}</Text>}
      >
        {!!selectedProductionSiteFieldDefinitionForUpdate && (
          <ProductionSiteFieldDefinitionUpdateForm
            activityNames={activities}
            organizationId={orgId}
            productionSiteFieldDefinition={
              selectedProductionSiteFieldDefinitionForUpdate
            }
            close={closeUpdateDrawer}
          />
        )}
      </Drawer>
    </>
  )
}
